<template>
  <long-query-layout
    resource-name="pudding-s"
    :query-filter="queryFilter"
    :map-query-filter-for-api="mapQueryFilterForApi"
    :apply-query-filter="applyQueryFilter"
    :result-fetch-url.sync="resultTable.url"
    :columns.sync="resultTable.columns"
    :group-headers.sync="resultTable.groupHeaders"
    :active-tab.sync="activeTab"
    :customers.sync="customerCollection"
    :excel-properties-handler="excelPropertiesHandler.bind(this)"
    :convert-menu-default-query-filter="convertMenuDefaultQueryFilter"
    @result-area-size-change="updateTableSize"
    @query-completed="onQueryCompleted"
  >
    <!-- -------------------------------------------------------- -->
    <!-- ヘッダのアクション -->
    <!-- -------------------------------------------------------- -->
    <template v-slot:header-actions="slotProps">
      <gl-switch
        :value="isDetail"
        :disabled="!(slotProps.queryId && activeTab == 'result')"
        @input="toggleDetail"
        >詳細</gl-switch
      >
    </template>

    <!-- -------------------------------------------------------- -->
    <!-- 分析条件 -->
    <!-- -------------------------------------------------------- -->
    <template v-slot:filter>
      <!-- 期間 -->
      <query-filter-element id="term" title="期間" contentWidth="medium">
        <filter-term-select v-bind.sync="queryFilter.term" />
      </query-filter-element>

      <!-- 商品分類 -->
      <query-filter-element id="item" title="商品分類">
        <gl-select-company :company.sync="queryFilter.item.company" />
        <div>
          <md-radio v-model="queryFilter.item.selectMode" value="category">
            分類
            <gl-sub-text>で指定</gl-sub-text>
          </md-radio>
          <md-radio v-model="queryFilter.item.selectMode" value="item">
            商品
            <gl-sub-text>で指定</gl-sub-text>
          </md-radio>
        </div>
        <filter-item-category
          v-if="queryFilter.item.selectMode === 'category'"
          v-bind.sync="queryFilter.item"
        />
        <filter-item
          v-if="queryFilter.item.selectMode === 'item'"
          v-bind.sync="queryFilter.item"
        />
      </query-filter-element>

      <!-- 店舗 -->
      <query-filter-element id="store" title="店舗">
        <filter-store v-bind.sync="queryFilter.store" />
      </query-filter-element>

      <!-- 顧客グループ -->
      <query-filter-element id="customer" title="顧客グループ">
        <filter-customer v-bind.sync="queryFilter.customer" />
      </query-filter-element>

      <!-- オプション -->
      <query-filter-element id="option" title="オプション">
        <filter-option v-bind.sync="queryFilter.option" />
      </query-filter-element>
    </template>

    <!-- -------------------------------------------------------- -->
    <!-- 分析結果 -->
    <!-- -------------------------------------------------------- -->
    <template v-slot:result>
      <gl-table ref="myTable" v-bind="resultTable" />
    </template>
  </long-query-layout>
</template>

<script>
// 分析条件, 分析結果のためのレイアウト
import LongQueryLayout from '@/components/layout/LongQueryLayout.vue'
// 分析条件
import QueryFilterElement from '@/components/QueryFilterElement.vue'
import FilterTermSelect from '@/components/FilterTermSelect.vue'
import FilterItemCategory from '@/components/FilterItemCategory.vue'
import FilterItem from '@/components/FilterItem2.vue'
import FilterStore from '@/components/FilterStore.vue'
import FilterCustomer from '@/components/FilterCustomer.vue'
import FilterOption from '@/components/FilterOption.vue'
// 分析条件モデル
import termSelect from '@/queryfilter/termSelect'
import item from '@/queryfilter/item2'
import store from '@/queryfilter/store'
import customer from '@/queryfilter/customer'
// 分析結果
import GlTable from '@/components/GlTable.vue'

import { trimAll } from '@/util/string'

let hold = false

function toggleDetail(colModelsRef, isDetail) {
  const groupColumns = colModelsRef
    .filter(c => c.headerContent && c.headerContent.headerContent === 'group')
    .map(c => c.colName)
  const detailColumns = colModelsRef
    .filter(c => c.headerContent && c.headerContent.headerContent === 'detail')
    .map(c => c.colName)

  colModelsRef.forEach(c => {
    // グループは詳細時は出さない
    if (groupColumns.includes(c.colName)) {
      c.hidden = isDetail
    }
    // 明細は詳細時に出す
    if (detailColumns.includes(c.colName)) {
      c.hidden = !isDetail
    }
  })
}

export default {
  components: {
    QueryFilterElement,
    FilterTermSelect,
    FilterItem,
    FilterItemCategory,
    FilterStore,
    FilterCustomer,
    FilterOption,
    LongQueryLayout,
    GlTable,
  },
  props: {
    /**
     * 自画面のドリルダウンや他画面からの遷移時に渡される引き継ぎID
     *
     * あくまで画面間で分析条件を引き継ぐための識別子で、分析時にサーバーで
     * 発行されるクエリIDとは別なので注意してください。
     *
     * メニューから最初に開く場合など、入力がない場合もあります。
     */
    inherit: {
      type: String,
    },
  },
  data() {
    return {
      /**
       * 表示タブ（分析条件、分析結果）
       *
       * 表示タブをプログラム側から切り替えたい場合をこれを使います。
       */
      activeTab: undefined,
      /**
       * 分析条件
       */
      queryFilter: {
        term: { order: 1, summaryKey: 'termSelect', ...termSelect.get() },
        item: { order: 2, summaryKey: 'itemGroup', ...item.get() },
        store: { order: 3, ...store.get() },
        customer: { order: 4, ...customer.get() },
        option: {
          order: 5,
          evaluationOption: 'ratio',
          customerSegment: 'rank',
          evaluations: ['customer_count', 'qty', 'amount', 'receipt_count'],
          errors: [],
          visibleEvaluationOptions: ['ratio', 'val'],
          visibleEvaluations: [
            'customer_count',
            'qty',
            'amount',
            'receipt_count',
          ],
        },
      },
      /**
       * 分析結果
       */
      resultTable: {
        columns: undefined,
        groupHeaders: undefined,
        sortColumnName: 'store_code',
        sortOrder: 'asc',
        url: undefined,
        width: 0,
        height: 0,
        customFormatter: {
          linkify: (rowData, cell, value, colName) => {
            cell.classList.add('customer-collection-cell')
            cell.setAttribute(
              'data-customer-collection',
              rowData.customer_filter[colName],
            )

            const toggle = e => {
              if (!this.resultTable.columns) {
                throw new Error('Column not found')
              }
              // hit the column
              const col = this.resultTable.columns.find(
                c => c.colName === colName,
              )

              const id = e.target.getAttribute('data-customer-collection')
              let text

              if (colName === 'customer_count') {
                text = trimAll(rowData.store_name)
              } else {
                text = `${trimAll(rowData.store_name)} - ${col.headerText}`
              }

              const customer = { id, text }

              this.$emit('toggle-customer-collection', customer)
            }

            cell.addEventListener('mousedown', e => {
              hold = true
              document.body.classList.add('no-select')
              toggle(e)
            })
            cell.addEventListener('mouseup', () => {
              hold = false
              document.body.classList.remove('no-select')
            })

            cell.addEventListener('mouseenter', e => {
              // console.log('hold', hold)
              if (!hold) {
                return
              }
              toggle(e)
            })
          },
        },
      },
      /**
       * この画面が持つ分析条件を、クエリのペイロードに変換する
       */
      mapQueryFilterForApi: queryFilter => ({
        term: termSelect.mapToApiPayload(queryFilter.term),
        item: item.mapToApiPayload(queryFilter.item),
        store: store.mapToApiPayload(queryFilter.store),
        customer: customer.mapToApiPayload(queryFilter.customer),
        option: {
          evaluationOption: queryFilter.option.evaluationOption,
          customerSegment: queryFilter.option.customerSegment,
          evaluations: queryFilter.option.evaluations,
        },
      }),
      /**
       * 渡された分析条件をこの画面の分析条件に反映する
       *
       * 渡される分析条件はこの画面のものとは限らないので、"可能な限り"復元する。
       */
      applyQueryFilter: queryFilter => {
        termSelect.applyPayload(
          this.queryFilter.term,
          queryFilter.term || queryFilter.termCompare,
        )
        const p = item.applyPayload(this.queryFilter.item, queryFilter.item)
        store.applyPayload(this.queryFilter.store, queryFilter.store)
        customer.applyPayload(this.queryFilter.customer, queryFilter.customer)
        this.queryFilter.option.evaluationOption =
          queryFilter.option.evaluationOption || 'ratio'
        this.queryFilter.option.customerSegment =
          queryFilter.option.customerSegment
        this.queryFilter.option.evaluations = queryFilter.option.evaluations

        return [p]
      },
      /**
       * 顧客コレクション
       */
      customerCollection: [],
      /**
       * 詳細
       */
      isDetail: false,
      /**
       * Excel出力のパラメータ生成
       */
      excelPropertiesHandler() {
        const { name, order } = this.$refs.myTable.getCurrentSort()
        return {
          sortColumnName: name,
          sortOrder: order,
          showDetail: this.isDetail,
        }
      },
    }
  },
  methods: {
    /**
     * 結果表示テーブルのサイズを更新する
     */
    updateTableSize(size) {
      this.resultTable.width = size.width
      this.resultTable.height = size.height
    },
    /**
     * クエリ完了
     */
    onQueryCompleted() {
      toggleDetail(this.resultTable.columns, this.isDetail)
      this.$refs.myTable.init()
    },
    /**
     * 詳細トグル
     */
    toggleDetail(value) {
      this.isDetail = value
      toggleDetail(this.resultTable.columns, value)
      this.$refs.myTable.init()
    },
    // デフォルトQueryFilterの変換処理
    // 商品グループは一時的なものなのでメニューのデフォルト条件
    // としては保持しない。
    convertMenuDefaultQueryFilter(queryFilter) {
      queryFilter.item.itemGroupId = undefined
      return queryFilter
    },
  },
}
</script>
